import ControllerBase from './../../../../global/js/ControllerBase';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpActionResponse } from '../../../../global/interfaces/http.interface';
import { KnowledgebaseSearchModel, KnowledgebaseSearchResponseSummaryModel } from './o-pdf-search-header.mock';
import { AtomButtonEvents } from 'wolf/components/atoms/a-button/a-button.events';
const template = require('components/molecules/m-typeahead-results/m-typeahead-results.template.twig') as any;

export default class ControllerPdfSearchHeader extends ControllerBase {
	/** A list of dom element references available to this component */
	private readonly componentRefs = {
		/** The form tag */
		form: 'form',
		/** The input for search queries */
		searchInput: 'searchInput',
		/** The filtered results container */
		filteredResults: 'filteredResults',
		/** The number of advanced search results based on the filters */
		filteredResultsCount: 'filteredResultsCount',
		/** The main submit button wrapper for the search */
		submitButtonWrapper: 'submitButtonWrapper',
		/** The advanced filters button */
		filterButton: 'filterButton',
	};

	private ajaxSearchUrl: string;
	/** A local cache for typeahead searches to reduce repeat queries */
	// private cache: Map<string, MoleculeTypeaheadResult[]>;

	private searchFiltersChanged: BehaviorSubject<KnowledgebaseSearchModel> = new BehaviorSubject({} as KnowledgebaseSearchModel);

	private searchResultSummaryChanged: BehaviorSubject<KnowledgebaseSearchResponseSummaryModel> = new BehaviorSubject(
		{} as KnowledgebaseSearchResponseSummaryModel,
	);

	private searchQuery: BehaviorSubject<HTMLInputElement> = new BehaviorSubject({} as HTMLInputElement);

	constructor(element: HTMLElement) {
		super(element);

		// this.ajaxSearchUrl = 'https://fetch.mock/o-pdf-search-header/get';
		this.ajaxSearchUrl = element.dataset.ajaxsearchurl ?? '';

		// run a new search when the advanced filters are changed
		this.searchFiltersChanged.subscribe((newFilters) => {
			if (newFilters && Object.keys(newFilters).length) {
				// run a new search
				this.queryResults();
			}
		});

		// update the search results summary number
		this.searchResultSummaryChanged.subscribe((newSummary) => {
			if (newSummary) {
				if (Object.keys(newSummary).length > 0) {
					const results = newSummary.results === undefined ? 0 : Number(newSummary.results);
					this.renderSearchSummary(results);
				}
				// else {
				// 	// handle errors silently and set to zero
				// 	this.renderSearchSummary(0);
				// }
			}
		});

		// this.cache = new Map();
		this.observeSearchInputChange();
		this.observeSearchButtonClick();
		this.observeFilterButtonClick();
		this.observeFilterChange();
	}

	/**
	 * Watch for changes to the search input when a user types
	 * @returns void
	 */
	private observeSearchInputChange(): void {
		const input = this.$references[this.componentRefs.searchInput];

		input &&
			fromEvent(input, 'input')
				.pipe(
					map((s) => {
						return s && (s.target as HTMLInputElement);
					}),
				)
				.subscribe((input) => {
					// input && this.filterResults(input);
					input && this.searchQuery.next(input);
				});
	}

	/**
	 * Watch for changes to the search advanced filter dropdowns
	 * @returns void
	 */
	private observeFilterChange(): void {
		const form = this.$references[this.componentRefs.form];
		if (form) {
			const selects = form.querySelectorAll<'select'>('select');
			selects &&
				selects.length &&
				selects.forEach((select) => {
					fromEvent(select, 'change').subscribe((s) => {
						s && s.target && this.filterResults(s.target as HTMLSelectElement);
					});
				});
		}
	}

	/**
	 * Watch for clicks on the advanced filter button, and submit the form
	 * @returns void
	 */
	private observeFilterButtonClick(): void {
		const filterButton = this.$references[this.componentRefs.filterButton];
		filterButton &&
			fromEvent(filterButton, 'click').subscribe((b: Event | MouseEvent) => {
				if (b && b.target) {
					b.preventDefault();
					this.submitForm();
				}
			});
	}

	/**
	 * Watch for clicks on the submit button
	 * @returns void
	 */
	private observeSearchButtonClick(): void {
		const elem = this.$references[this.componentRefs.submitButtonWrapper];
		const buttonAtomComponents = elem.querySelectorAll<'a'>('a');
		const buttonAtomComponent = buttonAtomComponents && buttonAtomComponents.length && buttonAtomComponents[0];

		const form = this.$references[this.componentRefs.form] as HTMLFormElement;
		if (form && buttonAtomComponent) {
			buttonAtomComponent.addEventListener(AtomButtonEvents.buttonClicked, (e: CustomEvent<Event | MouseEvent>) => {
				if (e && e.detail) {
					e.preventDefault();
					// submit the form
					form && this.submitForm();
				}
			});
		}
	}

	/**
	 * Submit the form
	 * @returns void
	 */
	private submitForm(): void {
		const form = this.$references[this.componentRefs.form] as HTMLFormElement;
		form && form.submit();
	}

	/**
	 * When a search filter changes, update the current filter object and trigger a new search
	 * @param  {HTMLSelectElement} et The event target
	 * @returns void
	 */
	private filterResults(et: HTMLSelectElement | HTMLInputElement): void {
		if (!et) {
			return;
		}
		const filterName: string = et.name;
		const filterValue: string = et.value;

		// build a new filter object from the existing filters with the new one's included
		const existingFilters = this.searchFiltersChanged.getValue()
			? Object.assign({}, this.searchFiltersChanged.getValue())
			: ({} as KnowledgebaseSearchModel);

		existingFilters[filterName] = filterValue;

		// add the input search value
		const searchQuery = this.searchQuery.getValue();
		if (searchQuery && searchQuery.name && searchQuery.value.toString().length > 0) {
			existingFilters[searchQuery.name] = searchQuery.value.toString();
		}

		this.searchFiltersChanged.next(existingFilters);
	}

	/**
	 * Render the search results summary onto the page
	 * @param  {number} numResults The number of results for the advanced search
	 * @returns void
	 */
	private renderSearchSummary(numResults: number): void {
		if (Object.prototype.hasOwnProperty.call(this.$references.hasOwnProperty, this.componentRefs.filteredResultsCount)) {
			this.$references[this.componentRefs.filteredResultsCount].innerHTML = '';

			// unhide the results readout
			const filterReadoutWrapper = this.$references[this.componentRefs.filteredResults];
			filterReadoutWrapper && filterReadoutWrapper.classList.remove('uk-invisible');

			// update the number of results found
			this.domUtils.renderDomElement(this.componentRefs.filteredResultsCount, numResults.toString());
		}
	}

	private getFormData(): FormData {
		const form = this.$references[this.componentRefs.form] as HTMLFormElement;
		const formData = new FormData(form);
		if (form) {
			// const formData = new FormData(form);

			const inputs = form.querySelectorAll('input, select');
			inputs.forEach((i: HTMLInputElement | HTMLSelectElement) => {
				formData.append(i.name, i.value);
			});
		}
		return formData;
	}

	/**
	 * Do an API call for results count summary (advanced search)
	 * @returns Promise
	 */
	private queryResults(): void {
		const url = new URL(this.ajaxSearchUrl);
		const form = this.$references[this.componentRefs.form] as HTMLFormElement;
		const inputs = form.querySelectorAll('input, select');
		// build query string
		inputs.forEach((i: HTMLInputElement | HTMLSelectElement) => {
			url.searchParams.set(i.name, i.value.toString());
		});

		// doesn't work?!
		// const formData = this.getFormData();

		// fetch(url.toString(), {
		// 	method: form.method,
		// 	body: formData,
		// 	// body: JSON.stringify(formData),
		// })
		fetch(url.toString())
			.then((response) => response.json())
			.then((resp: HttpActionResponse<KnowledgebaseSearchResponseSummaryModel, string>) => {
				if (resp) {
					if (resp.data) {
						// success
						this.searchResultSummaryChanged.next(resp.data);
						return resp.data;
					} else if (resp.message) {
						// not success

						// return resp.message;
						this.searchResultSummaryChanged.next({} as KnowledgebaseSearchResponseSummaryModel);
						return new Error(resp.message);
					}
				}
				this.searchResultSummaryChanged.next({} as KnowledgebaseSearchResponseSummaryModel);
				return new Error();
			})
			.catch((err: any) => {
				// catch 500 errors...
				// console.log(err);
				this.searchResultSummaryChanged.next({} as KnowledgebaseSearchResponseSummaryModel);
				return new Error(err);
			});
	}
}
